import React, { useState } from 'react';
import { Building2, ChevronDown, Check, Loader2, Plus, Copy } from 'lucide-react';
import { useTenant } from '@/contexts/TenantContext';
import { usePermissions } from '@/contexts/PermissionContext';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { CreateTenantDialog } from '@/components/settings/CreateTenantDialog';

interface TenantDropdownProps {
  collapsed?: boolean;
}

export function TenantDropdown({ collapsed = false }: TenantDropdownProps) {
  const { currentTenant, userTenants, switchTenant, isLoading: tenantLoading } = useTenant();
  const { hasPermission } = usePermissions();
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [switchingTo, setSwitchingTo] = useState<string | null>(null);
  const [isSwitching, setIsSwitching] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const handleTenantSwitch = async (tenantId: string) => {
    if (tenantId === currentTenant?.tenant_id || isSwitching) {
      setIsOpen(false);
      return;
    }

    try {
      setIsSwitching(true);
      setSwitchingTo(tenantId);
      const tenant = userTenants.find(t => t.tenant_id === tenantId);

      // Show switching toast
      toast({
        title: "Switching Tenant",
        description: `Switching to ${tenant?.company_name}...`,
      });

      // Wait for tenant switch to complete before redirecting
      await switchTenant(tenantId);

      // Redirect after successful switch
      window.location.replace('/dashboard');

    } catch (error) {
      console.error('Error switching tenant:', error);
      toast({
        title: "Error",
        description: "Failed to switch tenant. Please try again.",
        variant: "destructive",
      });
      setSwitchingTo(null);
      setIsSwitching(false);
    }
  };

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Copied!",
        description: `${label} copied to clipboard`,
        duration: 2000,
      });
    } catch (error) {
      console.error('Failed to copy:', error);
      toast({
        title: "Error",
        description: "Failed to copy to clipboard",
        variant: "destructive",
      });
    }
  };

  // Only show for superadmins with system.override permission
  if (!hasPermission('system.override')) {
    return null;
  }

  // Don't show if user only has one tenant or no tenants
  if (!userTenants || userTenants.length <= 1) {
    return null;
  }

  if (collapsed) {
    return (
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center justify-center w-full p-2 rounded-md text-sm font-medium transition-all duration-200 ease-in-out hover:bg-gray-600 hover:text-white"
          style={{ color: 'var(--sidebar-text-color, #ffffff)' }}
          title={currentTenant?.company_name || 'Select Tenant'}
        >
          <Building2 className="h-5 w-5" />
        </button>

        {isOpen && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 z-40" 
              onClick={() => setIsOpen(false)}
            />
            
            {/* Dropdown */}
            <div className="absolute left-full top-0 ml-2 w-64 bg-white rounded-lg shadow-lg border z-50">
              <div className="p-3 border-b">
                <h3 className="font-medium text-gray-900">Switch Tenant</h3>
              </div>
              <div className="py-2">
                {userTenants.map((tenant) => (
                  <div
                    key={tenant.tenant_id}
                    className="flex items-center justify-between w-full px-3 py-2 hover:bg-gray-50"
                  >
                    <button
                      onClick={() => handleTenantSwitch(tenant.tenant_id)}
                      disabled={switchingTo === tenant.tenant_id || isSwitching}
                      className="flex-1 min-w-0 text-left disabled:opacity-50"
                    >
                      <p className="font-medium text-gray-900 truncate">{tenant.company_name}</p>
                      <div className="flex items-center gap-1 group">
                        <p className="text-xs text-gray-500 truncate">ID: {tenant.tenant_id}</p>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(tenant.tenant_id, 'Tenant ID');
                          }}
                          className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 hover:bg-gray-200 rounded"
                          title="Copy tenant ID"
                        >
                          <Copy className="h-3 w-3 text-gray-600" />
                        </button>
                      </div>
                    </button>
                    <div className="flex items-center space-x-2 ml-2">
                      {switchingTo === tenant.tenant_id ? (
                        <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                      ) : currentTenant?.tenant_id === tenant.tenant_id ? (
                        <>
                          <Check className="h-4 w-4 text-green-600" />
                          <Badge variant="default" className="text-xs">Active</Badge>
                        </>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
              <div className="border-t">
                <button
                  onClick={() => {
                    setIsOpen(false);
                    setShowCreateDialog(true);
                  }}
                  className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create New Tenant
                </button>
              </div>
            </div>
          </>
        )}

        <CreateTenantDialog
          open={showCreateDialog}
          onOpenChange={setShowCreateDialog}
        />
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ease-in-out hover:bg-gray-600 hover:text-white"
        style={{ color: 'var(--sidebar-text-color, #ffffff)' }}
      >
        <div className="flex items-center min-w-0 flex-1">
          <Building2 className="h-5 w-5 mr-3 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            {tenantLoading ? (
              <div className="flex items-center space-x-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Loading...</span>
              </div>
            ) : currentTenant ? (
              <div className="truncate">
                <span className="font-medium">{currentTenant.company_name}</span>
              </div>
            ) : (
              <span className="text-gray-400">No tenant</span>
            )}
          </div>
        </div>
        <ChevronDown 
          className={cn("h-4 w-4 transition-transform duration-200", isOpen && "rotate-180")} 
        />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown */}
          <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border z-50">
            <div className="p-3 border-b">
              <h3 className="font-medium text-gray-900">Switch Tenant</h3>
            </div>
            <div className="py-2 max-h-64 overflow-y-auto">
              {userTenants.map((tenant) => (
                <div
                  key={tenant.tenant_id}
                  className="flex items-center justify-between w-full px-3 py-2 hover:bg-gray-50"
                >
                  <button
                    onClick={() => handleTenantSwitch(tenant.tenant_id)}
                    disabled={switchingTo === tenant.tenant_id || isSwitching}
                    className="flex-1 min-w-0 text-left disabled:opacity-50"
                  >
                    <p className="font-medium text-gray-900 truncate">{tenant.company_name}</p>
                    <div className="flex items-center gap-1 group">
                      <p className="text-xs text-gray-500 truncate">ID: {tenant.tenant_id}</p>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(tenant.tenant_id, 'Tenant ID');
                        }}
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 hover:bg-gray-200 rounded"
                        title="Copy tenant ID"
                      >
                        <Copy className="h-3 w-3 text-gray-600" />
                      </button>
                    </div>
                  </button>
                  <div className="flex items-center space-x-2 ml-2">
                    {switchingTo === tenant.tenant_id ? (
                      <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                    ) : currentTenant?.tenant_id === tenant.tenant_id ? (
                      <>
                        <Check className="h-4 w-4 text-green-600" />
                        <Badge variant="default" className="text-xs">Active</Badge>
                      </>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t">
              <button
                onClick={() => {
                  setIsOpen(false);
                  setShowCreateDialog(true);
                }}
                className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create New Tenant
              </button>
            </div>
          </div>
        </>
      )}

      <CreateTenantDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
      />
    </div>
  );
}
